import { WDSSortField, WIZ_COMMAND_STATE } from '@wizeoo/akm-core-lib';
import {
  WizActionObj,
  WizDatasource,
  WizIcon,
  WizPopUp,
  WizRoot,
  WizTabButton,
  WizEventManager,
  IWizRoute,
} from '@wizeoo/akm-component-lib';
import { BusinessException, EMPackEntry, EntityModificationPack, Tab } from '@wizeoo/akm-message-lib';
import { EM_ACTION } from '@wizeoo/akm-compute-lib';
import { cloneDeep } from 'lodash-es';

import { Model } from '../../model/model';
import { TabVO } from '../../vo/workingzone/tab.vo';
import { EMEngine } from '../entities/entity-modification.engine';
import { CommitCmd } from '../../command/em/commit.cmd';
import { ViewData } from '../../model/view.data';
import { NavigationParameter } from '../../manager/routing/navigation.parameter';
import { CtxtEntry } from '../context/user-context.service';
import { ALK_EVENT_TYPE, CONTEXT_TYPE } from '../../model/enums';
import { AlkEvent } from '../../framework/events/alk.event';
import { StaticFeatureEnum } from '../../manager/permission/permissions.types';
import { AttachmentService } from '../../command/core/attachment/attachment.service';


/** version 'lite' du tab pour persistence du contexte */
export interface WizContextTabObj {
  code?: string; // le code de l'onglet
  kind?: string; // 'static' | 'template';
  featureCode?: string; // la fonctionnalité correpondante
  order?: number; // l'ordre dans la tabbar
  viewOrder?: number; // l'ordre dans la vue des tabs
  lineColor?: string; // la couleur du liseré
}

export class TabEngine {
  
  protected model: Model = null;
  constructor(model: Model) {
    this.model = model;
  }
  
  public static COLOR_BASIC: string = '#818181';

  // Champ de tri des tabs
  private _tabSortField: WDSSortField = { property: 'order', way: 'asc', numeric: true };

  /* ################################################
             création des tabs
  ################################################ */

  /** création d'un nouvel élément */
  public newTabs(): Tab {
    const phyT: Tab = new Tab();
    phyT.domain = this.model.domainCode;
    phyT.businessUnitCode = this.model.businessUnitCode;
    phyT.visionCode = this.model.visionCode;
    return phyT;
  }

  /**
   * Effectue un clone du physical template
   * @param src La source à cloner
   */
  public clone(src: TabVO): TabVO {
    return new TabVO(this.model, cloneDeep(src.source));
  }

  /** recherche par code feature vision|codeTab */
  public getTabByCode(feature: string): TabVO {
    const map: Map<string, TabVO> = new Map(this.model.tabsCollection.map((item) => [item.featureCode, item]));
    return map.get(feature);
  }

  /**
   * create/ update d'un onglet
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
  public merge(to: TabVO, from: TabVO, callback: Function): void {
    let pack: EntityModificationPack;
    if (to.exists) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }

    if (!!pack && !!pack.ems && pack.ems.length > 0) {
      // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(to, WIZ_COMMAND_STATE.SUCCESS); // retour en vue
    }
  }

  /**
   * modification de l'ordre des onglets dans la vue newTab
   * @param fromTO collection [source, clone]
   * @param callback retour dans la vue
   */
  public mergeAll(fromTO: [TabVO, TabVO][], callback: Function): void {
    let pack: EntityModificationPack = null;
    let src: TabVO[] = [];
    fromTO.forEach((ft:[TabVO, TabVO])  => {
      if(!pack) {
        pack = EMEngine.mergeEM(ft[1], ft[0]);
      } else {
        EMEngine.addMergeEM(ft[1], ft[0], pack)
      }
      src.push(ft[0]);
    });


    if (pack?.ems?.length > 0) {
      // a minima une modif, sinon, c'est le même objet (update)
      // console.log('mergeAll', pack); // TODO gérer le mode sur le commit / ou faire un autre commit
      new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), src, callback).setReference('NEWTAB_ORDER').execute();
    } 
  }

  /**
   * delete d'un tab
   * @param to le clone à supprimer
   * @param callback retour dans la vue
   */
  public remove(to: TabVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.removeEM(to);
    new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      let vo: TabVO;
      let source: any;
      let packStatus: string;
      if(cmd.reference === 'NEWTAB_ORDER') { // on a modifié l'ordre des tabs dans la vue NewTab, updates en masse
        // console.log('backFromTabs', cmd.sourceVO, cmd.result.entries);
        // on a poussé les vo de la vue dans cmd.sourceVO
        let viewVOMap: Map<string, TabVO> = new Map();
        cmd.sourceVO.forEach((ivo:TabVO) => {
          viewVOMap.set(ivo.featureCode, ivo);
        });



        let precursorMap: Map<string, WizActionObj> = new Map(); // mise à jour du modèle, sinon désynchro
        this.availableTabs.forEach((wao: WizActionObj) => {
          precursorMap.set(wao.featureCode, wao);
        });


        cmd.result.entries.forEach((emp: EMPackEntry) => {
          
          let tab: Tab = emp.entity as Tab;
          let feature: string = [tab.visionCode,tab.code].join(ViewData.KEY_SEPARATOR);
          const fromView: TabVO = viewVOMap.get(feature);
          if (!!fromView) {
            fromView.source = tab;
          }

          

          const fromPrecursor: WizActionObj = precursorMap.get(feature);
          if (!!fromPrecursor) {
            fromPrecursor.viewOrder = tab.viewOrder;
          }

          const modelPhyT: TabVO = this.getTabByCode(feature);
          if (!!modelPhyT) {
            modelPhyT.source = tab;
          }

        });
        cmd.viewCallBack(cmd.sourceVO); // retour en vue
      } else { // modifs unitaires
        // le VO source
        vo = cmd.sourceVO[0] as TabVO;
        // une seule modification à la fois, packEntry donne l'objet
        source = cmd.result.entries[0].entity;
        // l'action (un seul type)
        const operation: string = cmd.result.ems[0].operation;
        // la liste d'erreurs métier
        const errors: BusinessException[] = cmd.result.errors;
        // le statut du pack
        packStatus = cmd.result.status;

        if (packStatus === WIZ_COMMAND_STATE.FAULT) {
          errors.forEach((be: BusinessException) => {
            WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
          });
        } else {
          switch (operation) {
            case EM_ACTION.UPDATE:
              vo.source = source; // le clone
              const modelPhyT: TabVO = this.getTabByCode(vo.featureCode);
              if (!!modelPhyT) {
                modelPhyT.source = source;
              }
              break;
            case EM_ACTION.CREATE:
              vo.source = source;
              this.model.tabsCollection.push(vo);
              this.addTemplateTab(vo);
              break;
            case EM_ACTION.DELETE:
              vo.source = source;
              const removed: TabVO[] = this.model.tabsCollection.splice(
                this.model.tabsCollection.indexOf(vo),
                1
              );
              break;
          }
        }
        cmd.viewCallBack(vo, packStatus); // retour en vue
      } 
      
    }
  }

  /* ################################################
        contexte et alimentation de la barre
  ################################################ */

  /**
   * alimentation des matIcons sur les vues classiques AKM
   * @param nav 
   */
  private _featureIconMap: Map<string, string> = null;
  private featureToIcon(nav: IWizRoute): string {
    if(!this._featureIconMap) {
      let map = new Map();
      map.set(StaticFeatureEnum.VISION, "wb_sunny");
      map.set(StaticFeatureEnum.VISION_PROFILE, "groups");
      map.set(StaticFeatureEnum.CHRONOCODE, "timelapse");
      map.set(StaticFeatureEnum.FREEDATA, "format_list_bulleted");
      map.set(StaticFeatureEnum.INCEXC, "join_left");
      map.set(StaticFeatureEnum.MEASURE, "device_thermostat");
      map.set(StaticFeatureEnum.SCENARIO, "history_edu");
      map.set(StaticFeatureEnum.WG, "scale");
      map.set(StaticFeatureEnum.CONNECT, "cable");
      map.set(StaticFeatureEnum.STYLE, "brush");
      map.set(StaticFeatureEnum.FORMAT, "emoji_symbols");
      map.set(StaticFeatureEnum.CSSRENDER, "css");
      map.set(StaticFeatureEnum.WORKFLOW, "task");
      map.set(StaticFeatureEnum.TEMPLATE, "view_comfy");
      map.set(StaticFeatureEnum.VISION_GENERATE, "construction");
      map.set(StaticFeatureEnum.VISION_USER, "person");
      map.set(StaticFeatureEnum.MATRIX, "hive");
      this._featureIconMap = map;
    }
    // console.log('nav', nav.featureCode, this._featureIconMap.get(nav.featureCode));
    return "mat|" + this._featureIconMap.get(nav.featureCode);
    // console.log("nav", nav);
    // return 'mat|favorite';
  }

  /**
   * Tous les tabs disponibles non filtrées
   */
  private _precursors: WizActionObj[];
  public get availableTabs(): WizActionObj[] {
    if (!this._precursors) {
      const precursors: NavigationParameter[] = [];

      this.model.routes.forEach(roots => {
        // on veut les vue de niveau 2
        if(roots.children?.length > 0) {
          roots.children.forEach(precursor => {
            if(!!precursor.showInTabs) {// une vrai vue admin
              precursors.push(
                new NavigationParameter(
                  precursor.path,
                  precursor.label,
                  precursor.featureCode,
                  this.featureToIcon(precursor), // 'users-management',
                  null,
                  precursor.color,
                  null,
                  precursor.description
                )
              );
            }
          });
        }
      });

      this._precursors = precursors.map((prec) => this.navigationParameterToTabObj(prec));
      
      const customTabs: WizActionObj[] = [];
      if (this.model.tabsCollection) {
        
        this.model.tabsCollection.forEach((t: TabVO) => {
          if(t.businessUnitCode === this.model.businessUnitCode && t.visionCode === this.model.visionCode) {
            customTabs.push(this.tabVOToTabObj(t));
          }
        });

        this._precursors = this._precursors.concat(customTabs);
       }
     }

    return this._precursors;
  }

  /** génération de l'objet de conf tabbar correspondant */
  private navigationParameterToTabObj(nav: NavigationParameter): WizActionObj {
    const result: WizActionObj = new WizActionObj(
      nav.code,
      nav.icon,
      null,
      WizTabButton,
      false,
      null,
      nav.width + 'px',
      null,
      nav.name
    );
    result.featureCode = nav.featureCode;
    result.order = nav.order;
    result.data = nav;
    result.lineColor = nav.color;
    result.kind = ViewData.KIND_STATIC;
    result.description = nav.description;
    return result;
  }

  /** génération de l'objet de conf tabbar correspondant, depuis un tab custom */
  private tabVOToTabObj(tab: TabVO): WizActionObj {
    const result: WizActionObj = new WizActionObj(
      tab.code,
      null,
      null,
      WizTabButton,
      false,
      null,
      '120px',
      null,
      tab.trsLabel
    );

    if(tab.icon) { // pour les tabs, ce sont des images
      result.imageSource = 'img|' + tab.icon;
      result.imgObs = AttachmentService.getInstance().downloadAttachmentForWidget(tab.icon, false);
    }

    result.featureCode = tab.featureCode; // dans ce cas, c'est le code vision|code tab
    result.order = 100;
    result.viewOrder = tab.viewOrder;
    result.data = tab;
    result.lineColor = tab.color ? tab.color : TabEngine.COLOR_BASIC;
    result.kind = ViewData.KIND_DYNAMIC;
    result.description = tab.trsDesc;
    return result;
  }

  /** génération de l'objet de conf tabbar correspondant */
  /**
   * création d'un objet TabContextEntry depuis le WizActionObj
   * @param wao WizActionObj
   * @param indexWao position
   * @returns TabContextEntry
   */
  public waoToContext(wao: WizActionObj, indexWao: number ): CtxtEntry {
    let contextKey: string = this.model.userContextService.buildKey(wao.featureCode);
    let res: CtxtEntry = {
      contextKey: contextKey,
      // featureCode: wao.featureCode,
      // lineColor: wao.lineColor,
      kind: wao.kind,
      // spécifique contexte
      tabId: wao.featureCode,
      login: this.model.connectedUser.mail,
      revisionNumber: 0,
      range: CONTEXT_TYPE.TAB,
      data: {order: indexWao}
    }
    return res;
  }

  /**
   * Refresh des tabs sur changement de vision (ou init)
   * @param forceVisionInTabs ajouter de force l'onglet vision, pour ne pas perdre l'onglet sur création de vision (on a forcément les droits, on y est)
   * @returns la collection de tabs
   */
  public refreshTabs(forceVisionInTabs: boolean = false): WizDatasource<WizActionObj> {
    
    // recharger le contexte user, plutot que tout buter
    this._userContextTabs = this.model.userContextService.getTabs();
    this._precursors = null;
    if (forceVisionInTabs) {
      this.forceTabFromView(this.model.favoriteRoute);
    } else {
      // On se contente de signaler la mise à jour des context tabs
      // Ca permet de mettre à jour aussi les currentTabs
      this._onContextTabsUpdated();
    }

    // console.log('refresh tab before return', this._userContextTabs, this._currentTabs);
    return this.currentTabs;
  }

  /** les onglets disponibles (avec application du filtre) */
  private _currentTabs: WizDatasource<WizActionObj>;

  public get currentTabs(): WizDatasource<WizActionObj> {
    if (!this._currentTabs) {
      const curTab: WizActionObj[] = this._buildCurrentTabsSource();
      this._currentTabs = new WizDatasource<WizActionObj>(curTab)
        .setFilter(this.filterContextTabs.bind(this))
        .setSortFields([this._tabSortField])
        .build();
      // console.log('get currenTabs from void', curTab)
    }
    return this._currentTabs;
  }

  /** Construction du currentTabs à partir des tabs du context utilisateur */
  private _buildCurrentTabsSource(): WizActionObj[] {
    // console.log('_buildCurrentTabsSource1', this._userContextTabs);
    const tabKeys: Set<string> = new Set();
    const result: WizActionObj[] = [];
    this.userContextTabs.forEach((wcto: CtxtEntry)=> {
      const res: WizActionObj = this._findTab(wcto.tabId);
      if (res) {
        res.order = wcto.data?.order;
        // res.viewOrder = wcto.data?.viewOrder;
        if (!tabKeys.has(res.buttonCode)) {
          result.push(res);
          tabKeys.add(res.buttonCode);
        }
        if (!res.label || res.label.length === 0) {
          // console.log('no label');
        }
      } else {
        // console.log('non exist tab', wcto);
      }
    });
    // console.log('_buildCurrentTabsSource', result);
    return result;
  }

  /** Recherche d'un onglet par code */
  private _findTab(pCode: string): WizActionObj {
    let exist: WizActionObj = null;
    this.availableTabs.forEach((wao: WizActionObj) => {
      if(wao.featureCode === pCode) {
        exist = wao;
      }
    })
    return exist;
    // return this.availableTabs?.find(({ buttonCode }) => buttonCode === pCode);
  }

  /**
   * Méthode filtrant toutes les tabs disponibles en fonction de l'état du context
   * @param pTab La tab à filtrer
   */
  private filterContextTabs(item: WizActionObj): boolean {
    let relatedContextTab: CtxtEntry = null;
    this.userContextTabs?.forEach((wcto: CtxtEntry) => { // attention, dans les contextes, c'est le featureCode dans code
      if(item.featureCode === wcto.tabId) {
        relatedContextTab = wcto;
      }
    });
    const res: boolean = relatedContextTab && this.model.permissionEngine.canRead(relatedContextTab.tabId);
    return res;
  }


  /* ################################################
            les tabs du contexte
    ################################################ */

  /** Les tabs filtrées au context de l'utilisateur. Si pas de contexte, on retourne les tabs par défaut */
  private _userContextTabs: CtxtEntry[];
  
  public selectTabFromContext:boolean = true;  // est ce qu'on sélectionne l'onglet à partir du contexte ? 

  // TODO synchroniser cette merde sur chargement... il ne reflete pas les Tabs....
  public get userContextTabs(): CtxtEntry[] {
    // console.log('tabEngine get userContextTabs', this._userContextTabs);
    if (!this._userContextTabs) {
      this._userContextTabs = this.model.userContextService.getTabs();
    }

    // console.log('user context tabs', this._userContextTabs);
    return this._userContextTabs;
  }

  /**
   * Mise à jour des tabs du context avec la liste des tabs (vue newTab)
   * @param items Les tabs sélectionnés
   */
  public updateContextTabs(items: WizActionObj[]) {
    this._userContextTabs = items.map((tab, indexTab) => this.waoToContext(tab, indexTab));
    this.saveContextTabs();
  }

  /**
   * Méthode mettant à jour les tabs du contexte
   */
  private saveContextTabs(): void {
    // console.log('saveContextTabs1', this._currentTabs);
    this.model.userContextService.saveTabs(this._userContextTabs);
    this._onContextTabsUpdated();
  }

  /** refresh sur modif du libellé / de la couleur de fond */
  public refreshAppearance(): void {
    this._onContextTabsUpdated();
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.TAB_COLLECTION_CHANGE, this.currentTabs));
  }

  private _onContextTabsUpdated() {
    // console.log('_onContextTabsUpdated1', this._currentTabs);
    const selection = this.currentTabs.selectedItem;
    // Force le refresh du currentTabs à partir des tabs du context utilisateur
    this.currentTabs.setSource(this._buildCurrentTabsSource());
    // console.log('_onContextTabsUpdated2', this._currentTabs);
    const newSelection = this.currentTabs.find((t) => t.buttonCode === selection?.buttonCode);
    this.currentTabs.select(!!newSelection ? [newSelection] : []);
  }

  /**
   * Supprime une Tab de la liste des tabs
   * @param pTab La tab à enlever (WizActionObj)
   */
  public removeTab(pTab: WizActionObj): void {
    const tab = this._userContextTabs.find((t) => t.tabId === pTab.featureCode);
    if (!!tab) {
      const tabIndex: number = this._userContextTabs.indexOf(tab);
      if (tabIndex >= 0) {
        this._userContextTabs.splice(tabIndex, 1);
      }
      this.model.userContextService.deleteTab(pTab.featureCode);
      this._onContextTabsUpdated();
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.REMOVE_TAB, pTab));
    }
  }

  public addTab(pTab: WizActionObj) {
    if (!!this.availableTabs) {
      this.availableTabs.push(pTab);
    }
    if (!!this.currentTabs) {
      this.currentTabs.addItem(pTab);
      this.updateContextTabs(this.currentTabs.getWorkedSource()); // ajout au contexte
    }
  }

  /** ajout d'un nouvel onglet */
  public addTemplateTab(pTab: TabVO) {
    this.addTab(this.tabVOToTabObj(pTab));
  }

  // forcer l'apparition d'une vue dans la barre d'onglets
  public forceTabFromView(view: String): WizActionObj {
    let tab: WizActionObj = null;

    // on prend le contexte et on s'assure qu'il contient la vue
    if (!this.userContextTabs || !this._userContextTabs.find((t) => t.tabId === view)) {
      // console.log('refresh tab vision patch', this.userContextTabs);
      if (!this._userContextTabs) { // pas de contexte, on va en créer un nouveau
        this._userContextTabs = [];
      }

      // collecte de l'onglet
      tab = this.availableTabs.find((t) => t.buttonCode === view);
      const context: CtxtEntry = this.waoToContext(tab, this._userContextTabs.length);
      this._userContextTabs.push(context);
    }

    // Sauvegarde du contexte
    this.saveContextTabs();
    return tab;
  }
}
